import type { SnapshotEntity } from "./types";

const DAMAGE_FLASH_DURATION_MS = 115;
const HP_DROP_EPSILON = 0.01;

const damageFlashUntil = new Map<string, number>();
const previousDamageFlag = new Map<string, boolean>();
const previousHp = new Map<string, number>();

export function registerDamageFlash(entity: SnapshotEntity, now: number): boolean {
  const key = entityKey(entity);
  const active = Boolean(entity.damage_flash ?? entity.damageFlash);
  const previousActive = previousDamageFlag.get(key) ?? false;
  const hp = entityHp(entity);
  const oldHp = previousHp.get(key);
  const hpDropped = hp !== null && oldHp !== undefined && hp < oldHp - HP_DROP_EPSILON;
  const hpDropFlashAllowed = hpDropped && shouldFlashOnHpDrop(entity);
  const flashStarted = active && !previousActive;

  if (flashStarted || hpDropFlashAllowed) {
    damageFlashUntil.set(key, now + DAMAGE_FLASH_DURATION_MS);
  }

  previousDamageFlag.set(key, active);

  if (hp !== null) {
    previousHp.set(key, hp);
  }

  return hpDropFlashAllowed || flashStarted;
}

export function damageFlashColor(entity: SnapshotEntity, now: number): string | null {
  const until = damageFlashUntil.get(entityKey(entity));

  if (until === undefined || until <= now) {
    return null;
  }

  const progress = clamp01(1 - (until - now) / DAMAGE_FLASH_DURATION_MS);

  if (progress < 0.25) {
    return interpolateColor("#5b1020", "#ef233c", progress / 0.25);
  }

  if (progress < 0.5) {
    return interpolateColor("#ef233c", "#ffffff", (progress - 0.25) / 0.25);
  }

  if (progress < 0.75) {
    return interpolateColor("#ffffff", "#ef233c", (progress - 0.5) / 0.25);
  }

  return interpolateColor("#ef233c", "#5b1020", (progress - 0.75) / 0.25);
}

export function cleanupDamageFlash(now: number, entities: SnapshotEntity[]): void {
  const visible = new Set(entities.map(entityKey));

  for (const [key, until] of damageFlashUntil) {
    if (until <= now || !visible.has(key)) {
      damageFlashUntil.delete(key);
    }
  }

  for (const key of previousDamageFlag.keys()) {
    if (!visible.has(key)) {
      previousDamageFlag.delete(key);
    }
  }

  for (const key of previousHp.keys()) {
    if (!visible.has(key)) {
      previousHp.delete(key);
    }
  }
}

function shouldFlashOnHpDrop(entity: SnapshotEntity): boolean {
  return normalizedKind(entity.kind) !== "zombie";
}

function normalizedKind(kind: unknown): string {
  return String(kind).replace(/[_-]/g, "").toLowerCase();
}

function entityHp(entity: SnapshotEntity): number | null {
  const hp = entity.hp;

  if (typeof hp !== "number" || !Number.isFinite(hp)) {
    return null;
  }

  return hp;
}

function entityKey(entity: SnapshotEntity): string {
  if (typeof entity.id === "number" || typeof entity.id === "string") {
    return `${entity.kind}:${entity.id}`;
  }

  if (entity.id && typeof entity.id === "object" && "0" in entity.id) {
    const nested = (entity.id as { 0?: unknown })[0];

    if (typeof nested === "number" || typeof nested === "string") {
      return `${entity.kind}:${nested}`;
    }
  }

  return `${entity.kind}:${entity.position.x}:${entity.position.y}:${entity.radius}`;
}

function interpolateColor(from: string, to: string, amount: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const t = easeInOut(clamp01(amount));

  return `rgb(${Math.round(lerp(a.r, b.r, t))}, ${Math.round(lerp(a.g, b.g, t))}, ${Math.round(lerp(a.b, b.b, t))})`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const value = Number.parseInt(hex.replace("#", ""), 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255
  };
}

function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

function easeInOut(value: number): number {
  return value < 0.5 ? 2 * value * value : 1 - Math.pow(-2 * value + 2, 2) / 2;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}